/* eslint-disable no-underscore-dangle */
import { Logger } from 'vue-infra';
import { each, get, map, isEqual, sortBy, includes } from 'lodash';
import { ROOT_GETTER_PATHS } from '@core/store';
import {
  METRIC_NAMES,
  RETRY_INTERVAL,
  DATA_LOADING_STATES,
  WIDGET_LOGICAL_NAMES,
  UPDATE_SUBSCRIPTION_MODE,
} from '@config/Consts';
import { getInterceptor, getSubscriptionsByMode, isMaxNumRetriesReached, isEventRelevant } from '@core/utils/utils';
import { Subscription } from '@core/common/Subscription';
import SkillsListService from '@core/services/skill-list/SkillsListService';
import { RESOURCES_METRICS_MAP, RESOURCES_MODEL_MAP, SKILL_LIST_RESOURCES } from './Consts';

const logger = Logger.getLogger('skillsList');

const SUBSCRIPTION_BY_MODE = {
  [UPDATE_SUBSCRIPTION_MODE.PRIMARY]: [
    SKILL_LIST_RESOURCES.METRICS,
  ],
  [UPDATE_SUBSCRIPTION_MODE.SECONDARY]: [
    SKILL_LIST_RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH,
  ],
};

const _getSubscriptionsByMode = async (mode, getters) => {
  const allSubscriptions = await SkillsListService.getSkillsListSubscriptions({
    interceptor: getInterceptor.bind(this, getters),
  });
  const subscriptions = getSubscriptionsByMode(allSubscriptions, SUBSCRIPTION_BY_MODE, mode);
  return subscriptions;
};

const _createSubscriptionPayload = (getters) => {
  logger.debug('Skill List::actions::_createSubscriptionPayload');
  return {
    timeframe: getters.getTimeFrameFilter,
    currentPageSkillIds: getters.getCurrentPageSkillIds.toString(),
    filter: {
      skills: getters.getSkillsFilter,
      groupIds: getters.getFilteredGroupsIds,
    },
    paging: getters.getPaginationConfigForAPI,
    sort: getters.getSort,
  };
};

const _getSkillsMetricsData = (responseData, resourceName) => {
  const skillsMetrics = [];

  each(responseData, (skillData) => {
    const skillItem = { id: skillData.id };
    const metrics = [];
    if (skillData && skillData.metrics instanceof RESOURCES_MODEL_MAP[resourceName]) {
      each(RESOURCES_METRICS_MAP[resourceName], (metricName) => {
        let score = skillData.metrics[metricName];
        if (metricName === METRIC_NAMES.SKILL_ASSIGNED_CONVERSATIONS
          || metricName === METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS) {
          score = get(skillData.metrics, metricName, 0);
        }
        metrics.push({ metric: metricName, score });
      });
      skillItem.metrics = metrics;
    }
    skillsMetrics.push(skillItem);
  });

  return skillsMetrics;
};

const _handleMetricsResource = ({ commit, getters, rootGetters }, resourceName, responseData) => {
  logger.debug('Skill List::actions::_handleMetricsResource');
  let dataStatus;
  let skillsData;
  let skillsMetrics = [];

  if (responseData) {
    dataStatus = DATA_LOADING_STATES.SUCCESS;
    skillsData = responseData.data;
    skillsMetrics = _getSkillsMetricsData(skillsData, resourceName);

    if (!isNaN(responseData.skillsTotalCount)) {
      commit('setPagination', { key: 'totalPages', value: responseData.skillsTotalCount });
    }

    const responseSkillds = map(skillsData, 'id');
    if (!isEqual(sortBy(responseSkillds), sortBy(getters.getCurrentPageSkillIds))) {
      commit('setCurrentPageSkillIds', responseSkillds);
      // eslint-disable-next-line no-use-before-define
      updateSubscription({ commit, getters, rootGetters },
        { subscriptionsMode: UPDATE_SUBSCRIPTION_MODE.SECONDARY });
    }
  } else if (getters.dataHasBeenFetched) {
    dataStatus = DATA_LOADING_STATES.OBSOLETE;
  } else {
    dataStatus = DATA_LOADING_STATES.ERROR;
  }

  commit('updateSkillsMetrics', { skillsMetrics, resourceName, dataStatus });
};

const _handleQueueHealthResource = ({ commit, getters }, resourceName, responseData) => {
  logger.debug('Skill List::actions::_handleQueueHealthResource');
  let dataStatus = DATA_LOADING_STATES.NONE;
  let skillsMetrics;

  if (responseData) {
    dataStatus = DATA_LOADING_STATES.SUCCESS;
    skillsMetrics = _getSkillsMetricsData(responseData.data, resourceName);
  } else if (getters.dataHasBeenFetched) {
    dataStatus = DATA_LOADING_STATES.OBSOLETE;
  }

  commit('updateSkillsMetrics', { skillsMetrics, resourceName, dataStatus });
};

const _handleSubscriptionSuccess = ({ commit, getters, rootGetters },
  resourceName, response = {}) => {
  logger.debug('Skill List::actions::_handleSubscriptionSuccess');
  const responseData = response.data;

  switch (resourceName) {
    case SKILL_LIST_RESOURCES.METRICS:
      _handleMetricsResource({ commit, getters, rootGetters }, resourceName, responseData);
      break;
    case SKILL_LIST_RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH:
      _handleQueueHealthResource({ commit, getters }, resourceName, responseData);
      break;
    default:
      break;
  }
};

const _onSubscriptionUpdateNotification = ({ commit, getters, rootGetters },
  resourceName, response) => {
  logger.debug('Skill List::actions::_onSubscriptionUpdateNotification', { resourceName, response });
  _handleSubscriptionSuccess({ commit, getters, rootGetters }, resourceName, response);
};

const _onSubscriptionError = ({ commit, getters, rootGetters }, resourceName, err) => {
  if (!err.cancelled) {
    logger.error('Skill List::actions::_onSubscriptionError', JSON.stringify({ resourceName, err }));
    // set the error state if it's a real error and not when we intentionally stop the request
    commit('setResourceDataStatus', { resourceName, dataStatus: DATA_LOADING_STATES.ERROR });
  }
  if (isMaxNumRetriesReached(err)) {
    const subscriptionsMode =
      includes(SUBSCRIPTION_BY_MODE[UPDATE_SUBSCRIPTION_MODE.PRIMARY], resourceName) ?
        UPDATE_SUBSCRIPTION_MODE.PRIMARY : UPDATE_SUBSCRIPTION_MODE.SECONDARY;
    const data = { subscriptionsMode };
    // eslint-disable-next-line no-use-before-define
    setTimeout(() => updateSubscription({ commit, getters, rootGetters }, data), RETRY_INTERVAL);
  }
};

const _handleSubscriptions = async ({ commit, getters, rootGetters }, subscriptions) => {
  if (subscriptions && subscriptions.subscriptions) {
    each(subscriptions.subscriptions, (subscription) => {
      commit('setDataStatus', { resourceName: subscription.name, dataStatus: DATA_LOADING_STATES.LOADING });
      subscription.subscription.clearAllEventListeners();
      subscription.subscription.on(Subscription.EVENTS.onSubscriptionUpdateNotification,
        _onSubscriptionUpdateNotification.bind(this,
          { commit, getters, rootGetters }, subscription.name),
        'skillsSubscriptionUpdateNotificationSuccess');
      subscription.subscription.on(Subscription.EVENTS.onSubscriptionError,
        _onSubscriptionError.bind(this, { commit, getters, rootGetters }, subscription.name),
        'skillsSubscriptionUpdateNotificationError');
      const subscriptionPayload = _createSubscriptionPayload(getters);
      try {
        logger.debug('Skill List::actions::before executing start subscription');
        subscription.subscription.start({ settings: subscriptionPayload,
          name: subscription.name });
      } catch (ex) {
        logger.error('cannot start Skills List subscription', 'start', { ex, subscription });
      }
    });
  }
};

const startSubscription = async ({ commit, getters, rootGetters }) => {
  logger.debug('Skill List::actions::startSubscription');
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.SKILLS)) {
    logger.debug('Skill List::no permission to start the skill list widget subscription');
    return;
  }
  const subscriptions = await SkillsListService.getSkillsListSubscriptions({
    interceptor: getInterceptor.bind(this, getters),
  });
  await _handleSubscriptions({ commit, getters, rootGetters }, subscriptions);
};

const stopSubscription = async ({ commit, getters, rootGetters }) => {
  logger.debug('Skill List::actions::stopSubscription');
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.SKILLS)) {
    logger.debug('Skill List::no permission to stop the skill list widget subscription');
    return;
  }
  const subscriptions = await SkillsListService.getSkillsListSubscriptions({
    interceptor: getInterceptor.bind(this, getters),
  });
  if (subscriptions && subscriptions.subscriptions) {
    each(subscriptions.subscriptions, (subscription) => {
      subscription.subscription.clearAllEventListeners();
      try {
        logger.debug('Skill List::actions::before executing stop subscription');
        subscription.subscription.stop({ name: subscription.name });
        commit('setDataStatus',
          { resourceName: subscription.name, dataStatus: DATA_LOADING_STATES.NONE });
      } catch (ex) {
        logger.error('cannot stop Skills List subscription', 'stop', { ex, subscription });
      }
    });
  }
};

const updateSubscription = async ({ commit, getters, rootGetters }, data) => {
  logger.debug('Skill List::actions::updateSubscription');
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.SKILLS)) {
    logger.debug('Skill List::no permission to update the skill list widget subscription');
    return;
  }
  const DEFAULT_UPDATE_SUBSCRIPTION_DATA = {
    resetPageNumber: true,
    subscriptionsMode: UPDATE_SUBSCRIPTION_MODE.PRIMARY,
  };
  const dataParams = Object.assign(DEFAULT_UPDATE_SUBSCRIPTION_DATA, data);

  if (
    dataParams.subscriptionsMode === UPDATE_SUBSCRIPTION_MODE.PRIMARY
    && dataParams.resetPageNumber
  ) {
    commit('setPagination', { key: 'currentPage', value: 1 });
  }

  const subscriptions =
    await _getSubscriptionsByMode(dataParams.subscriptionsMode, getters);

  if (subscriptions) {
    each(subscriptions, async (subscription) => {
      const subscriptionPayload = _createSubscriptionPayload(getters);
      try {
        const updateData = { settings: subscriptionPayload, name: subscription.name };
        await subscription.subscription.update(updateData);
      } catch (ex) {
        logger.error('cannot update Skills List subscription', 'update', { ex, subscription });
      }
    });
  }
};

const changeSort = ({ commit, getters, rootGetters }, sort) => {
  commit('setSort', sort);
  return updateSubscription({ commit, getters, rootGetters });
};

const applyPresetValues = ({ commit }, preset) => {
  commit('setPresetValues', preset);
};

const handleStartSubscription = ({ commit, getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.SKILLS, widgets)) {
    startSubscription({ commit, getters, rootGetters });
  }
};
const handleStopSubscription = ({ commit, getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.SKILLS, widgets)) {
    stopSubscription({ commit, getters, rootGetters });
  }
};
const handleUpdateSubscription = ({ commit, getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.SKILLS, widgets)) {
    const primarySubscriptionsData =
    {
      updateAgentStatusApi: true,
    };
    const secondarySubscriptionsData =
    {
      subscriptionsMode: UPDATE_SUBSCRIPTION_MODE.SECONDARY,
    };
    updateSubscription({ commit, getters, rootGetters }, primarySubscriptionsData);
    updateSubscription({ commit, getters, rootGetters }, secondarySubscriptionsData);
  }
};
const handleShowWidgets = ({ commit, getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.SKILLS, widgets)) {
    startSubscription({ commit, getters, rootGetters });
  } else {
    stopSubscription({ commit, getters, rootGetters });
  }
};

export default {
  RESOURCES_METRICS_MAP,
  SUBSCRIPTION_BY_MODE,
  UPDATE_SUBSCRIPTION_MODE,
  changeSort,
  applyPresetValues,
  handleStartSubscription,
  handleStopSubscription,
  handleUpdateSubscription,
  handleShowWidgets,
  updateSubscription, // needed for pagination
};
